import { assertCompleteErrorBody } from "../errors/index.js";
/**
 * الشكل الذي يعيده السيرفر عند الفشل، قبل التحقّق منه.
 *
 * **وما ليس من مغلّفنا ليس خطأً في كودنا.**
 *
 * فكان الغيابُ يُقرأ `INTERNAL` — «حصل خطأ غير متوقّع عندنا، اتسجّل وهنشوفه» —
 * **ورقمُ المرجع يُولَّد محلياً فلا يكون في سجلٍّ أصلاً**. فمن يبعثه يُبحَث له
 * عن رقمٍ لم يُكتَب، ومن يقرأ الرسالة يظنّ أن عطباً عندنا بينما **السيرفرُ لم
 * يردّ من أصله**: بروكسيٌّ أو بوّابةٌ أو حاجزُ شبكةٍ ردّ مكانَه. وهو أسوأُ
 * ما يصل شادي عن تركيبِ مشترٍ: يحقّق في كودٍ سليم.
 *
 * فجوابٌ بلا `code` ولا `messageKey` **خدمةٌ لا تستجيب** — ورسالةُ
 * `DEPENDENCY_UNAVAILABLE` تقول ذلك، وعلاجُها «راجع شاشة صحّة النظام».
 * ويُوسَم رقمُه `local: true`، **فيُعرَف أنه من المتصفّح لا من السيرفر**.
 */
function asErrorBody(payload, fallbackCorrelationId) {
    const body = (payload ?? {});
    const ours = typeof body.code === "string" && typeof body.messageKey === "string";
    if (!ours) {
        return {
            code: "DEPENDENCY_UNAVAILABLE",
            messageKey: "errors.dependencyUnavailable",
            correlationId: fallbackCorrelationId,
            context: { local: true, reason: "response-not-ours" },
        };
    }
    return {
        code: body.code ?? "INTERNAL",
        messageKey: body.messageKey ?? "errors.internal",
        correlationId: body.correlationId ?? fallbackCorrelationId,
        ...(body.fields ? { fields: body.fields } : {}),
        ...(body.context ? { context: body.context } : {}),
    };
}
export function createApiClient(options) {
    const base = options.baseUrl.replace(/\/$/, "");
    async function request(path, request = {}) {
        const correlationId = options.newCorrelationId();
        const method = request.method ?? "GET";
        const headers = { accept: "application/json" };
        if (request.body !== undefined)
            headers["content-type"] = "application/json";
        if (request.rawBody)
            headers["content-type"] = request.rawBody.contentType;
        /**
         * **ولا طلبَ مُغيِّرٌ بلا مفتاح.**
         *
         * فالسيرفر يرفض بدونه، والرفضُ خطأٌ برمجيّ لا خطأُ مستخدم. والمفتاح
         * المولَّد هنا فريدٌ لكل نقرة — وهو الصواب: نقرتان قصدُهما فعلان.
         */
        if (method !== "GET") {
            headers["idempotency-key"] = request.idempotencyKey ?? options.newCorrelationId();
        }
        else if (request.idempotencyKey) {
            headers["idempotency-key"] = request.idempotencyKey;
        }
        let response;
        try {
            response = await options.fetchImpl(`${base}${path}`, {
                method,
                headers,
                credentials: "include",
                ...(request.rawBody
                    ? { body: request.rawBody.bytes }
                    : request.body !== undefined
                        ? { body: JSON.stringify(request.body) }
                        : {}),
                ...(request.signal ? { signal: request.signal } : {}),
            });
        }
        catch (cause) {
            /**
             * الشبكة لم تصل أصلاً — فلا رقم ربط من السيرفر.
             *
             * نولّد واحداً هنا ليبقى للمستخدم **رقمٌ يقوله**، ويُميَّز بأنه محلّي.
             * البديل — رسالة «فشل الاتصال» بلا شيء يُقتفى — هو ما لا نقبله.
             */
            const aborted = cause instanceof Error && cause.name === "AbortError";
            return {
                ok: false,
                error: {
                    code: aborted ? "REQUEST_ABORTED" : "DEPENDENCY_UNAVAILABLE",
                    messageKey: aborted ? "errors.requestAborted" : "errors.dependencyUnavailable",
                    correlationId,
                    context: { local: true, path, method },
                },
            };
        }
        if (response.status === 204) {
            return { ok: true, data: undefined, correlationId };
        }
        /**
         * **والإلغاءُ قد يقع أثناء قراءة الجسم** — بعد أن ردّ السيرفر.
         *
         * وكان الالتقاطُ حول الطلب وحده، **فإلغاءٌ أثناء القراءة يفلت رفضاً بلا مالك**:
         * شاشةٌ تُغلَق في منتصف ردّها تُظهر «حصل خطأ غير متوقّع» على ما لم يفشل.
         */
        let text;
        try {
            text = await response.text();
        }
        catch (cause) {
            const aborted = cause instanceof Error && cause.name === "AbortError";
            return {
                ok: false,
                error: {
                    code: aborted ? "REQUEST_ABORTED" : "DEPENDENCY_UNAVAILABLE",
                    messageKey: aborted ? "errors.requestAborted" : "errors.dependencyUnavailable",
                    correlationId,
                    context: { local: true, path, method },
                },
            };
        }
        let payload = undefined;
        if (text.length > 0) {
            try {
                payload = JSON.parse(text);
            }
            catch {
                payload = undefined;
            }
        }
        if (response.ok) {
            /**
             * **السيرفر يغلّف كل نجاح** بـ`{ data, correlationId, feedback? }` — فيُفضّ هنا مرة
             * واحدة لا في كل شاشة. كنتُ أعيد المغلّف كما هو، فوصلت القدرات `undefined`
             * إلى القوقعة **فانكمشت القائمة إلى بند واحد بلا خطأ واحد**. ولم تمسكه الاختبارات
             * لأنها كانت تردّ أجساماً من صنعي لا من صنع السيرفر.
             */
            const envelope = payload;
            const wrapped = envelope !== null && typeof envelope === "object" && "correlationId" in envelope;
            if (!wrapped)
                return { ok: true, data: payload, correlationId };
            const feedback = envelope.feedback;
            return {
                ok: true,
                data: envelope.data,
                correlationId: envelope.correlationId ?? correlationId,
                ...(feedback ? { feedback } : {}),
            };
        }
        const error = asErrorBody(payload, correlationId);
        assertCompleteErrorBody(error);
        if (error.code === "SESSION_INVALID")
            options.onSessionInvalid?.();
        if (method !== "GET" && (error.code === "FORBIDDEN" || error.code === "SCOPE_FORBIDDEN")) {
            options.onRefused?.(method, path.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id"), error);
        }
        return { ok: false, error };
    }
    return { request };
}
//# sourceMappingURL=client.js.map